import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'

// Canonical value sent to the server, label shown to staff. 'Broken' is the
// stored grade for "Spares or Repair"; 'Mint' shows as "Like New".
const CONDITION_CHOICES = [
  { value: 'Mint', label: 'Like New' },
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Very Good', label: 'Very Good' },
  { value: 'Good', label: 'Good' },
  { value: 'Fair', label: 'Fair' },
  { value: 'Poor', label: 'Poor' },
  { value: 'Broken', label: 'Spares or Repair' },
]

const BASED_ON_NOTICE = {
  testing: 'Built from the original testing record.',
  product_details: 'No testing record was found for this item, so this uses the title, type and condition only. Check every detail.',
}

/**
 * Drafts a new title, condition tag and description for a chosen condition from
 * the listing template. It only hands them back through onChange as unsaved
 * edits. Staff review them in the pop-up and save with its normal Save button.
 * Undo puts all three back.
 */
function DescriptionRebuild({ productId, title, description, saved, onChange }) {
  const [condition, setCondition] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [draft, setDraft] = useState(null) // { previous, basedOn } while a draft is unsaved

  // Once the pop-up saves, the draft is the saved text: there is nothing to undo.
  useEffect(() => {
    if (saved) setDraft(null)
  }, [saved])

  const choice = CONDITION_CHOICES.find(c => c.value === condition)

  const handleRebuild = async () => {
    setLoading(true)
    setError('')
    try {
      const result = await api.post(`/api/inventory/shopify/product/${productId}/description-draft`, { condition })
      setDraft(prev => ({ previous: prev ? prev.previous : { title, description }, basedOn: result.based_on }))
      onChange({ title: result.title, description: result.description_html, condition: result.condition })
    } catch (e) {
      setError(e.message || 'Could not draft a description')
    } finally {
      setLoading(false)
    }
  }

  const handleUndo = () => {
    onChange({ ...draft.previous, condition: undefined })
    setDraft(null)
  }

  return (
    <div className="description-rebuild">
      <label className="form-label" htmlFor="rebuild-condition">Rebuild description for</label>
      <select
        id="rebuild-condition"
        className="form-input"
        value={condition}
        onChange={e => setCondition(e.target.value)}
      >
        <option value="">Choose a condition</option>
        {CONDITION_CHOICES.map(c => (
          <option key={c.value} value={c.value}>{c.label}</option>
        ))}
      </select>
      {choice && (
        <button type="button" className="btn btn-secondary btn-sm" onClick={handleRebuild} disabled={loading}>
          {loading ? 'Drafting...' : `Rebuild description for ${choice.label}`}
        </button>
      )}
      {error && <p className="form-error" role="alert">{error}</p>}
      {draft && (
        <div className="description-rebuild-notice" role="status">
          <strong>Drafted from the listing template: title, condition tag and description. Review before saving.</strong>
          <span> {BASED_ON_NOTICE[draft.basedOn]}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleUndo}>Undo</button>
        </div>
      )}
    </div>
  )
}

export default DescriptionRebuild
